/**
 * The one observation shape every source is normalised into.
 *
 * Every source kind (an airport report, a hangar anemometer, a manual entry) is turned
 * into an `Observation` before anything else looks at it. The limits engine, the screens
 * and every integrating app read this shape and nothing else, which is what lets a dropzone
 * add a new kind of station without any of them changing.
 *
 * Two conventions carry the whole design:
 *
 * - **`null` means "this source did not measure it".** Not zero, not calm, not clear.
 *   A Davis anemometer reports no ceiling; an airport report has no lull. The limits
 *   engine treats a `null` reading as no evidence either way, never as "within limits".
 * - **Times are UTC epoch milliseconds** (`UtcMs`). Airport reports are issued in Zulu
 *   time; the suite's usual "dropzone-local naive timestamp" convention would make
 *   "observed 14 min ago" wrong by the UTC offset. Convert to site-local only for display.
 *
 * Sources are never blended (SKW-R-002): an observation always belongs to exactly one
 * `sourceId`, and nothing in this package averages across sources.
 */

/** Milliseconds since the Unix epoch, UTC. */
export type UtcMs = number

export type SourceKind =
  | 'noaa-metar' // airport report (METAR/SPECI) from NOAA's Aviation Weather Center
  | 'noaa-taf' // airport forecast; produces forecasts, not observations
  | 'noaa-windtemp' // winds aloft; produces aloft forecasts, not surface observations
  | 'nws-forecast' // US National Weather Service hourly gridpoint forecast
  | 'awos-bridge' // an AWOS data port read on site by the bridge (report text)
  | 'davis-cloud' // Davis WeatherLink v2 cloud API
  | 'davis-local' // Davis WeatherLink Live on the local network, via the bridge
  | 'tempest' // WeatherFlow Tempest: the owner's own station only
  | 'ambient' // Ambient Weather REST / realtime
  | 'push-ecowitt' // a station's custom-server upload, Ecowitt format
  | 'push-wunderground' // a station's custom-server upload, Wunderground format
  | 'manual' // typed in by a person; carries who entered it

export interface SourceDescriptor {
  id: string
  siteId: string
  kind: SourceKind
  /** What the owner calls it. Shown in "Weather observations (<label>)". */
  label: string
  /** Height of the wind sensor above ground, metres, when known. Shown, never corrected for. */
  sensorHeightM?: number
  /**
   * How often this source is expected to report, in seconds. Past 1.5× this the source is
   * "late", past 3× it is "not reporting" (`staleness.ts`, shared with the limits engine;
   * an optional extra grace exists there but is off by default).
   */
  expectedIntervalS: number
}

export type CloudCover = 'FEW' | 'SCT' | 'BKN' | 'OVC' | 'VV'

export interface CloudLayer {
  cover: CloudCover
  /** Base above ground level, feet. */
  baseFtAgl: number
  /** e.g. 'CB' or 'TCU' when reported. */
  type?: 'CB' | 'TCU'
}

export interface Observation {
  sourceId: string
  observedAt: UtcMs
  /**
   * When the reading summarises a window (the bridge sends one summary per minute), its
   * length in seconds. `windKt` is then the window average, `gustKt` the window maximum and
   * `lullKt` the window minimum, so the number limits care about (the gust) is never lost.
   */
  windowS?: number

  /** Direction the wind is FROM, degrees true. `null` when calm, variable or not measured. */
  windDirDeg: number | null
  /** Reported as variable (METAR `VRB`). */
  windVariable: boolean
  /** METAR variable-direction range, e.g. `250V310`. */
  windVarFromDeg: number | null
  windVarToDeg: number | null
  /** Sustained / average wind, knots. `0` is a real calm reading; `null` is "not measured". */
  windKt: number | null
  gustKt: number | null
  lullKt: number | null

  /** Statute miles. METAR `10SM` and `P6SM` are recorded as their stated value. */
  visibilitySm: number | null
  /**
   * Lowest broken, overcast or vertical-visibility layer, feet above ground.
   * `'none'` = the source reported the sky and there is no ceiling; `null` = not measured.
   */
  ceilingFtAgl: number | 'none' | null
  clouds: CloudLayer[] | null

  tempC: number | null
  dewpointC: number | null
  altimeterInHg: number | null
  /** Any precipitation reported. `null` = not measured. */
  precipitation: boolean | null

  /** The source's own text when it has one (the METAR), for "raw on tap". */
  raw: string | null
  /** For `manual` sources: who typed it. */
  enteredBy?: string
}

/** An observation with every measurement unknown: the starting point for normalisers. */
export function emptyObservation(sourceId: string, observedAt: UtcMs): Observation {
  return {
    sourceId,
    observedAt,
    windDirDeg: null,
    windVariable: false,
    windVarFromDeg: null,
    windVarToDeg: null,
    windKt: null,
    gustKt: null,
    lullKt: null,
    visibilitySm: null,
    ceilingFtAgl: null,
    clouds: null,
    tempC: null,
    dewpointC: null,
    altimeterInHg: null,
    precipitation: null,
    raw: null,
  }
}
